import { ChevronRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { PostureChip } from '@/components/ui/posture-chip';
import { Button } from '@/components/ui/button';
import { rollupColorToPostureStatus, type OverallPostureDomain } from '@/lib/api/health-posture';
import { formatDate } from '@/lib/utils/format';

/**
 * Health-posture grid for the engagement overview (T-0097).
 *
 * Renders the engagement's per-HealthDomain latest-assessment rollup as
 * a 2-column grid of cards inside a parent card. Each card mirrors the
 * prototype's domain-row pattern in `screens-engagement.js → window.
 * Screens.overview` (the 2-column grid below "Health Check posture ·
 * N domains"):
 *
 *   ┌──────────────────────────────────────────┐
 *   │ ▌ Domain Name             [posture chip] │
 *   │ ▌ Optional one-line note                  │
 *   └──────────────────────────────────────────┘
 *
 * The 3px left border carries the posture color — mirrors the
 * `border-left: 3px solid var(--posture-{posture})` rule in the
 * prototype. We use `border-l-[3px]` plus a posture-token class.
 *
 * Posture chip vocabulary: the API surfaces RollupColor (green |
 * yellow_divergent | red | grey); we translate to the visual chip
 * status via `rollupColorToPostureStatus()` at this boundary. The
 * `<PostureChip>` primitive is the load-bearing UI element (README §6).
 */
export interface HealthPostureGridProps {
  engagementId: string;
  domains: OverallPostureDomain[];
  /**
   * Optional override for the "last assessed" date. When omitted the grid
   * derives it from the newest `latest_confirmed_at` across `domains`.
   */
  lastAssessedAt?: string | null;
}

const POSTURE_LEFT_RULE: Record<ReturnType<typeof rollupColorToPostureStatus>, string> = {
  healthy: 'border-l-posture-healthy',
  'at-risk': 'border-l-posture-at-risk',
  critical: 'border-l-posture-critical',
  unassessed: 'border-l-posture-unassessed',
};

export function HealthPostureGrid({
  engagementId,
  domains,
  lastAssessedAt,
}: HealthPostureGridProps) {
  // Stable display order via the API-provided field (matches the
  // prototype's left-to-right ordering across domains).
  const ordered = [...domains].sort((a, b) => a.display_order - b.display_order);

  // Caption facts come from the domains themselves, so the card can't
  // claim "no assessments" while showing rated domains. A domain counts
  // as assessed once it has a committed assessment. The newest
  // confirmation date wins; ISO strings compare chronologically.
  const assessedCount = ordered.filter((d) => d.latest_assessment_id !== null).length;
  const newestConfirmedAt = ordered.reduce<string | null>((max, d) => {
    const at = d.latest_confirmed_at ?? null;
    return at !== null && (max === null || at > max) ? at : max;
  }, null);
  const lastAssessed = lastAssessedAt ?? newestConfirmedAt;
  const caption =
    assessedCount === 0
      ? 'No committed assessments yet'
      : `${assessedCount} of ${ordered.length} assessed` +
        (lastAssessed ? ` · last assessed ${formatDate(lastAssessed)}` : '');

  return (
    <section
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
      aria-labelledby="health-posture-grid-title"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 id="health-posture-grid-title" className="t-h3">
            Health Check posture · {ordered.length} {ordered.length === 1 ? 'domain' : 'domains'}
          </h2>
          <p className="t-meta">{caption}</p>
        </div>
        <Button asChild variant="ghost" size="sm">
          {/* No row named: the screen opens on its first domain. */}
          <Link to="/engagements/$engagementId/health-readiness" params={{ engagementId }}>
            Open Health & Readiness
            <ChevronRight className="size-3" aria-hidden="true" />
          </Link>
        </Button>
      </div>

      {ordered.length === 0 ? (
        <p className="text-body-sm text-ink-500">
          No active health domains. Add them on Health & Readiness to start assessments.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {ordered.map((d) => {
            const status = rollupColorToPostureStatus(d.latest_rollup_color);
            return (
              <div
                key={d.domain_id}
                className={`flex items-center gap-2.5 rounded border border-ink-100 border-l-[3px] bg-paper-0 px-3 py-2.5 ${POSTURE_LEFT_RULE[status]}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-body-sm font-medium text-ink-900">{d.name}</div>
                  <div className="t-meta">
                    {d.latest_rating
                      ? `Latest rating: ${d.latest_rating.replace('_', ' ')}`
                      : 'Not assessed yet'}
                  </div>
                </div>
                <PostureChip status={status} />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
